import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ReactMarkdown from 'react-markdown';
import { marked } from 'marked';
import { useLocation } from 'react-router-dom';
import PptxGenJS from 'pptxgenjs';

import { Logo } from 'assets';
import { getJsonValue } from 'services/utils';
import { getfundDetails, updateFundStatusByID } from '../Slice/FundSlice';
import { setFundData } from '../Slice/FundDataSlice';

import PdfPortfolios from './PdfPortfolios';
import RecommendationSection from './RecommendationSection';
import ExecutiveSummarySection from './ExecutiveSummarySection';
import BenchmarkQuartiles from './BenchmarkQuartiles';
import ScoringMatrix from './ScoringMatrix';
import ValueCreation from './ValueCreation';
import PerformanceExecutive from './PerformanceExecutive';
import HistoricalFund from './HistoricalFund';
import PdfFundCompare from './PdfFundCompare';
import PdfFees from './pdfFees';
import { formatAmountMagnitude } from 'services/constants';
import { reportError, reportException } from 'services/logging';

const PdfViewer = ({
  fundKey,
  fundCompareData,
  directCall = 0,
  SelectedOptions = [],
  setLoader,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const shouldDownloadPpt = queryParams.get('downloadPpt') === '1';

  const portfolioData = useSelector((state) => state.portfolioData);
  const { fundData, displayUserConfig } = useSelector(
    ({ fundData }) => fundData
  );
  const { fundBenchmark } = useSelector((state) => state.fundData);

  const matrixData = useSelector((state) => state.scoringMatrix.matrixData);

  const [divisionData, setDivisionData] = useState(null);
  const [hasDownloaded, setHasDownloaded] = useState(false);

  const sectionsConfig =
    displayUserConfig?.data?.ic_report_ui_config?.sections || [];
  const fieldOrderConfig =
    displayUserConfig?.data?.client_ui_config?.sections || [];
  const fundDetailJson =
    fundData?.fund_data_in_json?.['ai-provided-data']?.[0]?.sections;

  const isBlackBookUrl = useMemo(() => {
    const base = `${window.location.origin}/black-book`;
    return directCall === 1
      ? window.location.href === `${base}/${fundKey}`
      : window.location.href === base;
  }, [directCall, fundKey]);

  const onMain = useCallback(() => navigate('/'), [navigate]);

  const renderMarkdown = (text) => (
    <ReactMarkdown
      components={{
        h2: ({ node, ...props }) => <h2 className="custom-h2" {...props} />,
        p: ({ node, ...props }) => <span {...props} />,
      }}
    >
      {text}
    </ReactMarkdown>
  );

  const convertToHTML = (text) => {
    return marked(text)
      .replace(//g, '<li>')
      .replace(/\n/g, '</li>')
      .replace(/<li>([\s\S]+?)<\/li>/g, '<ul>$&</ul>');
  };

  function generateTable(entries) {
    if (!entries || entries.length === 0) {
      return null;
    }

    return entries.map((entry, index) => {
      const feeType =
        entry.fee_type !== null && entry.fee_type !== 'null'
          ? entry.fee_type
          : '';
      const feeValue =
        entry.fee_value !== null && entry.fee_value !== 'null'
          ? renderMarkdown(entry.fee_value)
          : '';
      const feeDescription =
        entry.fee_description !== null && entry.fee_description !== 'null'
          ? renderMarkdown(entry.fee_description)
          : '';

      return (
        <p
          className={
            isBlackBookUrl
              ? `text-14-executive`
              : `web-typography-mini-executive`
          }
          key={index}
        >
          {feeType}:{' '}
          <span
            className={isBlackBookUrl ? 'web-span-mini-14' : `web-span-mini`}
          >
            {feeValue}. {feeDescription}
          </span>
        </p>
      );
    });
  }

  function getValueByKey(key) {
    if (!Array.isArray(fundDetailJson)) return '';
    for (const section of fundDetailJson) {
      for (const [sectionKey, values] of Object.entries(section)) {
        if (Array.isArray(values)) {
          for (const item of values) {
            if (item.hasOwnProperty(key)) {
              return item[key];
            }
          }
        } else if (values.values) {
          for (const item of values.values) {
            if (item.hasOwnProperty(key)) {
              return item[key];
            }
          }
        }
      }
    }
    return '';
  }

  const getSectionTitle = (config, key) =>
    config?.flatMap((sec) => sec.values || []).find((item) => item.key === key)
      ?.title || '';

  const getTableTitle = (sectionsConfig, key) => {
    return (
      sectionsConfig
        ?.flatMap((section) => section.values || [])
        ?.flatMap((subSection) => subSection.values || [])
        ?.find((item) => item.key === key)?.title || ''
    );
  };

  function generateTableFee(entries) {
    if (!entries || entries?.length === 0) {
      return null;
    }

    return entries?.map((entry, index) => {
      const feeType =
        entry?.fee_type !== null && entry?.fee_type !== 'null'
          ? entry?.fee_type
          : '';
      const feeValue =
        entry?.fee_value !== null && entry?.fee_value !== 'null'
          ? entry?.fee_value
          : '';
      const feeDescription =
        entry?.fee_description !== null && entry?.fee_description !== 'null'
          ? entry?.fee_description
          : '';

      return {
        key: getTableTitle(sectionsConfig, 'fee') && feeType,
        value: (
          <>
            {feeValue} – {renderMarkdown(feeDescription)}
          </>
        ),
      };
    });
  }

  // Get value for fees, portfolio-team, fund-assets
  function getValueForKey(entry, key) {
    let value = '';
    if (entry && entry.hasOwnProperty(key)) {
      value = entry[key] !== null && entry[key] !== 'null' ? entry[key] : '';
    }
    return value;
  }

  function getTitleByKey(key) {
    for (const section of fieldOrderConfig) {
      // Check if the section has values to search within
      if (section.values) {
        for (const item of section.values) {
          if (item.key === key) {
            return item.title;
          }
        }
      }
    }
    return null; // Return null if the key is not found
  }

  const fundJson = () => {
    // Iterate through each object in jsonData array
    let fundDetail = [];
    let fundManager = [];
    let fundTerms = [];
    let fundPerformance = [];
    let fundBenchmarks = [];
    let fundFees = [];
    let portfolioTeam = [];
    let fundAssets = [];
    const fundJson =
      fundData?.fund_data_in_json?.['ai-provided-data']?.[0]?.sections;
    // Iterate through each object in jsonData array
    fundJson?.forEach((obj) => {
      if (obj.hasOwnProperty('fund-details')) {
        fundDetail = obj['fund-details'];
      }
      if (obj.hasOwnProperty('fund-manager')) {
        fundManager = obj['fund-manager'];
      }
      if (obj.hasOwnProperty('fund-terms')) {
        fundTerms = obj['fund-terms'];
      }
      if (obj.hasOwnProperty('fund-performance')) {
        fundPerformance = obj['fund-performance'];
      }
      if (obj.hasOwnProperty('fund-benchmarks')) {
        fundBenchmarks = getJsonValue(obj['fund-benchmarks'], 'values');
      }
      if (obj.hasOwnProperty('fund-fees')) {
        fundFees = getJsonValue(obj['fund-fees'], 'values');
      }
      if (obj.hasOwnProperty('portfolio-team')) {
        portfolioTeam = getJsonValue(obj['portfolio-team'], 'values');
      }
      if (obj.hasOwnProperty('fund-assets')) {
        fundAssets = getJsonValue(obj['fund-assets'], 'values');
      }
    });

    const data = {
      fundDetail: fundDetail,
      fundManager,
      fundTerms,
      fundPerformance,
      fundBenchmarks,
      portfolioTeam,
      fundAssets,
      fundFees,
    };

    return setDivisionData(data);
  };

  useEffect(() => {
    fundJson();
  }, [fundData]);

  function keyValue(entries, key) {
    let value = '';
    if (Array.isArray(entries) && typeof key === 'string') {
      entries?.forEach((entry) => {
        if (entry?.hasOwnProperty(key)) {
          value =
            entry?.[key] !== 'null' && entry?.[key] !== null
              ? entry?.[key]
              : '';
        }
      });
    }
    return value;
  }

  const sendUpdateRequest = (status) => {
    dispatch(updateFundStatusByID({ fund_id: fundKey, fund_status: status }))
      .then((res) => {
        if (res?.payload?.data?.status === 200) {
          dispatch(getfundDetails(fundKey)).then((res) => {
            const fundDataRes = res?.payload?.data?.data;
            dispatch(setFundData(fundDataRes));
          });
          toast.success('Status has been successfully updated');
        }
      })
      .catch((err) => {
        reportException(err, 'send update request for pdf viewer');
      });
  };

  const renderSectionComponent = (sectionKey) => {
    switch (sectionKey) {
      case 'recommendation':
        return (
          <RecommendationSection
            getValueByKey={getValueByKey}
            getSectionTitle={getSectionTitle}
            isBlackBookUrl={isBlackBookUrl}
            renderMarkdown={renderMarkdown}
          />
        );
      case 'portfolio_name':
        return (
          <>
            <PdfPortfolios portfolioData={portfolioData} />
            <div className="page-break"></div>
          </>
        );
      case 'executive_summary':
        return (
          <ExecutiveSummarySection
            getValueByKey={getValueByKey}
            getSectionTitle={getSectionTitle}
            isBlackBookUrl={isBlackBookUrl}
            sectionsConfig={sectionsConfig}
            renderMarkdown={renderMarkdown}
            generateTable={generateTable}
            divisionData={divisionData}
            getValueForKey={getValueForKey}
          />
        );
      case 'benchmark_quartiles':
        return <BenchmarkQuartiles fundData={fundData} />;
      case 'scoring_matrix':
        return (
          <ScoringMatrix
            sectionsConfig={sectionsConfig}
            matrixData={matrixData}
          />
        );
      case 'value_creation':
        return (
          <ValueCreation
            sectionsConfig={sectionsConfig}
            getSectionTitle={getSectionTitle}
            renderMarkdown={renderMarkdown}
            getValueByKey={getValueByKey}
            convertToHTML={convertToHTML}
          />
        );
      case 'performance_expectations':
        return (
          <PerformanceExecutive
            sectionsConfig={sectionsConfig}
            PdfFees={PdfFees}
            generateTableFee={generateTableFee}
            getValueByKey={getValueByKey}
            divisionData={divisionData}
          />
        );
      case 'historical_fund_track_record':
        return (
          <HistoricalFund
            sectionsConfig={sectionsConfig}
            fundData={fundData}
            fundKey={fundKey}
          />
        );
      case 'fund_comparison':
        return (
          <PdfFundCompare
            sectionsConfig={sectionsConfig}
            fundCompareData={fundCompareData}
            fundKey={fundKey}
            SelectedOptions={SelectedOptions}
            setLoader={setLoader}
          />
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (
        shouldDownloadPpt &&
        fundData &&
        sectionsConfig?.length > 0 &&
        !hasDownloaded
      ) {
        generatePPT(fundBenchmark, matrixData, fundDetailJson);
        setHasDownloaded(true);
      }
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [
    shouldDownloadPpt,
    fundData,
    sectionsConfig,
    fundBenchmark,
    matrixData,
    hasDownloaded,
  ]);

  const generateRecommendationSlide = (
    pptx,
    fundData,
    displayUserConfig,
    getValueByKey,
    formatAmountMagnitude
  ) => {
    const slide = pptx.addSlide();

    // Title
    slide.addText('Recommendation', {
      x: 0.5,
      y: 0.3,
      w: 9,
      h: 0.5,
      fontSize: 20,
      bold: true,
      color: '000000',
    });

    let yPos = 1.0; // Starting Y position
    const lineHeight = 0.4;
    const fontSize = 12;
    const marginLeft = 0.5;

    // Get the configuration (fallback to your provided JSON if missing)
    const sectionConfig =
      displayUserConfig?.data?.ic_report_ui_config?.sections?.find(
        (sec) => sec.key === 'recommendation'
      ) || {
        sub_header_values: [
          { title: 'Asset Class', key: 'asset_class' },
          { title: 'Sub Asset Class', key: 'sub_asset_class' },
          {
            title: 'Fund Net Return Expectation',
            key: 'expected_net_returns_in_percent',
          },
          { title: 'Fund Relative Risk Evaluation', key: 'fund_relative_risk' },
        ],
        values: [
          { title: 'Recommendation', key: 'recommendation_amount' },
          {
            title: 'History with Fund Manager',
            key: 'history_with_fundmanager',
          },
        ],
      };

    // Helper function to render a field (with placeholder for empty values)
    const renderField = (title, value, options = {}) => {
      const displayValue = value ?? 'N/A'; // Fallback to "N/A" if null/undefined
      slide.addText(
        [
          { text: `${title}: `, options: { bold: true } },
          { text: displayValue, options: {} },
        ],
        {
          x: marginLeft,
          y: yPos,
          w: 9,
          h: 0.5,
          fontSize: fontSize,
          color: '363636',
          ...options,
        }
      );
      yPos += lineHeight;
    };

    // Process ALL sub-header fields (even if empty)
    sectionConfig.sub_header_values.forEach(({ key, title }) => {
      const value = getValueByKey(key) ?? fundData?.[key];

      // Special handling for Fund Net Return Expectation (MOIC + %)
      if (key === 'expected_net_returns_in_percent') {
        const moic = getValueByKey('expected_net_returns_in_moic') ?? 'N/A';
        const percent = value ?? 'N/A';
        const combinedText = `${moic} (MOIC) / ${percent}%`;
        renderField(title, combinedText);
      }
      // Default case for other fields
      else {
        renderField(title, value);
      }
    });

    // Process ALL main values (even if empty)
    sectionConfig.values.forEach(({ key, title }) => {
      const value = getValueByKey(key) ?? fundData?.[key];

      // Special handling for Recommendation (formatted amount + fund name)
      if (key === 'recommendation_amount') {
        const fundName = fundData?.fund_name ?? 'N/A';
        const formattedAmount = value
          ? formatAmountMagnitude({ value })
          : 'N/A';
        const recommendationText = `Commit ${formattedAmount} to ${fundName}`;
        renderField(title, recommendationText, { color: '363636' });
      }
      // Default case for other fields (e.g., History with Fund Manager)
      else {
        renderField(title, value);
      }
    });

    // Optional: Add summary if exists
    if (fundData?.recommendation_summary) {
      renderField('Summary', fundData.recommendation_summary);
    }
  };

  const generateExecutiveSummarySlide = (pptx, fundData, divisionData) => {
    const slide = pptx.addSlide();

    // Title (in a separate text box)
    slide.addText('Executive Summary', {
      x: 0.5,
      y: 0.3,
      fontSize: 20,
      bold: true,
    });

    const sectionsConfig =
      displayUserConfig?.data?.ic_report_ui_config?.sections || [];

    const executiveSection = sectionsConfig.find(
      (section) => section?.key === 'executive_summary'
    );

    const allClientKeys =
      displayUserConfig?.data?.client_ui_config?.sections
        ?.flatMap((section) => section.values || [])
        ?.map((item) => item.key) || [];

    const alwaysIncludeKeys = [
      'key_person_list',
      'management_fee',
      'fundraising_status',
    ];

    const filteredFields =
      executiveSection?.values?.filter(
        (field) =>
          field?.key &&
          (allClientKeys.includes(field.key) ||
            alwaysIncludeKeys.includes(field.key))
      ) || [];

    const keyPersons =
      divisionData?.portfolioTeam
        ?.map(
          (person) =>
            `${getValueForKey(person, 'first_name')} ${getValueForKey(
              person,
              'last_name'
            )}`
        )
        .join(', ') || '';

    const textChunks = [];

    filteredFields.forEach(({ key, title }) => {
      const value = getValueByKey(key);

      if (key === 'size') {
        const maxSize = getValueByKey('max_fund_size');
        const line = `${value || ''}${value && maxSize ? ' / ' + maxSize + ' max' : ''}`;
        textChunks.push(
          { text: `${title}: `, options: { bold: true } },
          { text: `${line}\n`, options: {} }
        );
      } else if (key === 'key_person_list') {
        textChunks.push(
          { text: `${title}: `, options: { bold: true } },
          { text: `${keyPersons || '[None]'}\n`, options: {} }
        );
      } else if (key === 'fundraising_status') {
        textChunks.push({ text: `${title}:\n`, options: { bold: true } });
      } else if (key === 'management_fee') {
        const feeEntries = divisionData?.fundFees || [];
        if (feeEntries.length > 0) {
          feeEntries.forEach((entry) => {
            const feeType = entry.fee_type || '';
            const feeValue = entry.fee_value || '';
            const feeDescription = entry.fee_description || '';

            textChunks.push(
              { text: `${feeType}: `, options: { bold: true } },
              { text: `${feeValue} – ${feeDescription}\n`, options: {} }
            );
          });
        } else {
          textChunks.push(
            { text: `${title}: `, options: { bold: true } },
            { text: '[No Fees Found]\n', options: {} }
          );
        }
      } else if (value && value !== 'null') {
        textChunks.push(
          { text: `${title}: `, options: { bold: true } },
          { text: `${value}\n`, options: {} }
        );
      }
    });

    if (textChunks.length === 0) {
      textChunks.push({ text: '[No data]', options: {} });
    }

    // Add all text in one text box (except the title)
    slide.addText(textChunks, {
      x: 0.5,
      y: 0.2,
      w: 9.0,
      h: 5.0,
      fontSize: 12,
      color: '363636',
      wrap: true,
      lineSpacing: 20,
      margin: 0.2,
    });
  };

  const generateBenchmarkSlide = (pptx, fundBenchmarka, sectionsConfig) => {
    const benchmarks = fundBenchmarka?.benchmarks || [];
    const asofDates = fundBenchmarka?.asof_dates || {};
    if (!benchmarks.length) return;

    const splitFieldsMap = {
      fund_net_irr_quartile: ['fund_net_irr', 'fund_irr_quartile'],
      fund_dpi_quartile: ['fund_net_dpi', 'fund_dpi_quartile'],
      fund_tvpi_quartile: ['fund_net_tvpi', 'fund_tvpi_quartile'],
    };

    const visibleKeys = [
      'fund_name_vintage',
      'target_size',
      'invested_amount',
      'realized_amount',
      'total_value',
      'fund_net_moic',
      'fund_net_irr_quartile',
      'fund_dpi_quartile',
      'fund_tvpi_quartile',
    ];

    // Table Headers
    const tableHeaders = visibleKeys.map((key) => {
      if (splitFieldsMap[key]) {
        const [firstKey, secondKey] = splitFieldsMap[key];
        const firstTitle = getSectionTitle(sectionsConfig, firstKey);
        const secondTitle = getSectionTitle(sectionsConfig, secondKey);
        return `${firstTitle} | ${secondTitle}`;
      } else {
        return getSectionTitle(sectionsConfig, key) || key;
      }
    });

    // Table Rows
    const tableRows = benchmarks.map((benchmark) => {
      const detail = benchmark.detail || {};
      const quartile = benchmark.quartile || {};
      return visibleKeys.map((key) => {
        switch (key) {
          case 'fund_name_vintage':
            return `${detail.benchmark_fund_name || 'N/A'} (${detail.vintage_year || 'N/A'})`;
          case 'target_size':
            return detail.target_size || 'N/A';
          case 'invested_amount':
            return detail.invested_amount || 'N/A';
          case 'realized_amount':
            return detail.realized_amount || 'N/A';
          case 'total_value':
            return detail.total_value || 'N/A';
          case 'fund_net_moic':
            return detail.fund_net_moic || 'N/A';
          case 'fund_net_irr_quartile':
            return `${detail.fund_net_irr || 'N/A'} | ${quartile.fund_irr_quartile || 'N/A'}`;
          case 'fund_dpi_quartile':
            return `${detail.fund_net_dpi || 'N/A'} | ${quartile.fund_dpi_quartile || 'N/A'}`;
          case 'fund_tvpi_quartile':
            return `${detail.fund_net_tvpi || 'N/A'} | ${quartile.fund_tvpi_quartile || 'N/A'}`;
          default:
            return 'N/A';
        }
      });
    });

    // Create slide
    const slide = pptx.addSlide();
    slide.addText('Benchmark Quartiles', {
      x: 0.5,
      y: 0.3,
      fontSize: 20,
      bold: true,
    });

    // Add table
    // Add table with optimized sizing
    slide.addTable([tableHeaders, ...tableRows], {
      x: 0.5,
      y: 1,
      w: '85%', // Slightly wider than 80% for better readability
      colW: [
        // Custom column widths for each column
        1.0, // Fund Name (Vintage) - wider for names
        1.0, // Target Size
        1.0, // Invested Amount
        1.0, // Realized Amount
        1.0, // Total Value
        1.0, // Fund Net MOIC
        1.0, // Fund Net IRR (%) | Quartile
        1.0, // Fund Net DPI | Quartile
        1.0, // Fund Net TVPI | Quartile
      ],
      fontSize: 9, // Slightly smaller font
      border: { type: 'solid', color: '888888', pt: 0.75 }, // Thinner borders
      align: 'center',
      valign: 'middle', // Better vertical alignment
      margin: 4, // Reduced cell padding
      lineSpacing: 10, // Tighter line spacing
    });

    // Add footers if available
    const footerLines = Object.entries(asofDates)
      .filter(([k, v]) => v)
      .map(([measure, date]) => `${measure} As of: ${date}`);

    if (footerLines.length) {
      slide.addText(footerLines.join('\n'), {
        x: 0.5,
        y: 5.2,
        fontSize: 9,
        color: '555555',
        w: '90%',
      });
    }
  };

  const generateScoringMatrixSlide = (pptx, matrixData) => {
    if (!Array.isArray(matrixData) || matrixData.length === 0) return;

    const rows = [];

    // Header row
    rows.push(['Name', 'Score', 'Rationale']);

    matrixData.forEach((groupItem) => {
      const groupKey = Object.keys(groupItem)[0];
      const group = groupItem[groupKey];

      // Main score
      rows.push([
        group.displayName || '',
        group.value?.toString() || '',
        group.description || '',
      ]);

      // Sub-scores
      const subScores = group['sub-scores'] || [];
      subScores.forEach((subItem) => {
        const subKey = Object.keys(subItem)[0];
        const sub = subItem[subKey];
        rows.push([
          sub.displayName || '',
          sub.value?.toString() || '',
          sub.description || '',
        ]);
      });
    });

    const slide = pptx.addSlide();
    slide.addText('Scoring Matrix', {
      x: 0.5,
      y: 0.3,
      fontSize: 20,
      bold: true,
    });

    slide.addTable(rows, {
      x: 0.5,
      y: 1,
      w: '90%',
      colW: [3.0, 1.0, 4.5],
      fontSize: 10,
      border: { type: 'solid', color: '999999', pt: 1 },
      align: 'left',
    });
  };

  const generateValueCreationSlide = (pptx, fundDetailJson, sectionsConfig) => {
    const slide = pptx.addSlide();
    slide.addText('Value Creation', {
      x: 0.5,
      y: 0.3,
      fontSize: 20,
      bold: true,
    });

    // Find the value creation section in the config
    const valueCreationSection = sectionsConfig.find(
      (section) => section.key === 'value_creation'
    );

    if (!valueCreationSection) {
      slide.addText('No Value Creation section configured', {
        x: 0.5,
        y: 1,
        fontSize: 14,
        color: '363636',
      });
      return;
    }

    // Find the value creation data in fundDetailJson
    let valueCreationData = [];
    for (const section of fundDetailJson) {
      if (section.hasOwnProperty('value_creation')) {
        // Check if it's an array or object with values property
        if (Array.isArray(section['value_creation'])) {
          valueCreationData = section['value_creation'];
        } else if (section['value_creation']?.values) {
          valueCreationData = section['value_creation'].values;
        }
        break;
      }
    }

    // If no specific value_creation section found, look for fields in other sections
    if (valueCreationData.length === 0) {
      // Check fund-details for relevant fields
      const fundDetails = fundDetailJson.find((s) =>
        s.hasOwnProperty('fund-details')
      );
      if (fundDetails) {
        valueCreationData = fundDetails['fund-details'].filter(
          (item) =>
            item.competitive_advantage_details ||
            item.investment_strategy_details ||
            item.portfolio_construction_details
        );
      }
    }

    // Get all client keys to filter fields
    const allClientKeys =
      displayUserConfig?.data?.client_ui_config?.sections
        ?.flatMap((section) => section.values || [])
        ?.map((item) => item.key) || [];

    // Fields to always include
    const excludeKey = ['competitive_landscape_summary'];

    // Filter fields that should be shown
    const filteredFields =
      valueCreationSection?.values?.filter(
        (field) =>
          field?.key &&
          (allClientKeys.includes(field.key) || excludeKey.includes(field.key))
      ) || [];

    // Collect all content lines
    const contentLines = [];

    filteredFields.forEach(({ key, title }) => {
      // Find the value for this key
      let value = '';

      // First check in valueCreationData
      if (Array.isArray(valueCreationData)) {
        for (const item of valueCreationData) {
          if (item.hasOwnProperty(key)) {
            value = item[key];
            break;
          }
        }
      }

      // If not found, try to get from getValueByKey
      if (!value || value === 'null') {
        value = getValueByKey(key);
      }

      // Skip if no value
      if (!value || value === 'null') return;

      // Clean up the value - remove HTML tags and markdown formatting
      let cleanValue = String(value)
        .replace(/<\/?[^>]+(>|$)/g, '') // Remove HTML tags
        .replace(/[*_`]/g, '') // Remove markdown formatting
        .replace(/\n/g, ' ') // Replace newlines with spaces
        .trim();

      // Add to content lines
      contentLines.push(`${title}:`);
      contentLines.push(cleanValue);
      contentLines.push(''); // Add empty line for spacing
    });

    // If no content was found
    if (contentLines.length === 0) {
      contentLines.push('No value creation data available');
    }

    // Add the content to the slide
    slide.addText(contentLines.join('\n'), {
      x: 0.5,
      y: 1,
      fontSize: 14,
      color: '363636',
      lineSpacing: 20,
      w: '90%',
      h: '80%',
      wrap: true,
    });
  };

  const generatePerformanceExpectationsSlide = (
    pptx,
    fundDetailJson,
    sectionsConfig,
    divisionData
  ) => {
    const slide = pptx.addSlide();
    slide.addText('Performance Expectations', {
      x: 0.5,
      y: 0.3,
      fontSize: 20,
      bold: true,
    });

    const performanceSection = sectionsConfig.find(
      (section) => section.key === 'performance_expectations'
    );

    if (!performanceSection) {
      slide.addText('No Performance Expectations section configured', {
        x: 0.5,
        y: 1,
        fontSize: 14,
        color: '363636',
      });
      return;
    }

    const allClientKeys =
      displayUserConfig?.data?.client_ui_config?.sections
        ?.flatMap((section) => section.values || [])
        ?.map((item) => item.key) || [];

    // Adjusted yPositions to reduce spacing
    const tables = [
      { title: 'Fund Level', rows: [], yPosition: 1.0 },
      { title: 'Fees And Expenses', rows: [], yPosition: 2.3 }, // Reduced from 3.0
      { title: 'Return Expectation', rows: [], yPosition: 3.6 }, // Reduced from 5.0
    ];

    performanceSection.values?.forEach((subSection) => {
      subSection.values?.forEach((field) => {
        if (
          !field?.key ||
          (!allClientKeys.includes(field.key) && field.key !== 'fee')
        )
          return;

        let value = getValueByKey(field.key) || '';
        let title = field.title || field.key;

        if (field.key === 'investment_term') {
          const duration = getValueByKey('fund_duration') || '';
          const extension = getValueByKey('extension') || '';
          value = `${duration} - ${extension}`;
        }

        let tableIndex = 0;
        if (
          field.key === 'carried_interest' ||
          field.key === 'management_fee' ||
          field.key === 'fee'
        ) {
          tableIndex = 1;
        } else if (
          field.key === 'net_irr' ||
          field.key === 'expected_net_returns_in_moic'
        ) {
          tableIndex = 2;
        }

        if (!value || value === 'null') return;

        let cleanValue = String(value)
          .replace(/<\/?[^>]+(>|$)/g, '')
          .replace(/[*_`]/g, '')
          .trim();

        tables[tableIndex].rows.push([title, cleanValue]);
      });
    });

    const feeEntries = divisionData?.fundFees || [];
    feeEntries.forEach((entry) => {
      const feeType = entry.fee_type || '';
      const feeValue = entry.fee_value || '';
      const feeDescription = entry.fee_description || '';
      tables[1].rows.push([feeType, `${feeValue} – ${feeDescription}`]);
    });

    // Calculate dynamic table heights
    let currentY = 1.0;
    tables.forEach((table) => {
      if (table.rows.length > 0) {
        const rowHeight = 0.4; // Height per row in inches
        const tableHeight = (table.rows.length + 1) * rowHeight; // +1 for header row

        const tableData = [[table.title, ''], ...table.rows];

        slide.addTable(tableData, {
          x: 0.5,
          y: currentY,
          w: '90%',
          colW: [2.5, 5.0],
          fontSize: 12,
          border: { type: 'solid', color: '888888', pt: 1 },
          align: 'left',
          valign: 'middle',
          margin: 4,
          autoPageHeaderRows: 1,
          headerRows: 1,
          headerRowColor: 'F2F2F2',
          headerRowFontFace: 'Arial',
          headerRowFontSize: 14,
          headerRowBold: true,
        });

        currentY += tableHeight + 0.2; // Reduced spacing between tables (0.2 inches)
      }
    });

    if (tables.every((table) => table.rows.length === 0)) {
      slide.addText('No performance expectations data available', {
        x: 0.5,
        y: 1,
        fontSize: 14,
        color: '363636',
      });
    }
  };

  const generateHistoricalTrackRecord = (pptx, fundData) => {
    // 1. Create slide with title
    const slide = pptx.addSlide();
    slide.addText('Historical Fund Track Record', {
      x: 0.5,
      y: 0.3,
      fontSize: 20,
      bold: true,
      color: '000000',
    });

    // 2. Safely extract benchmark data
    const benchmarks = [];
    try {
      const aiData =
        fundData?.fund_data_in_json?.['ai-provided-data']?.[0] || {};
      const sections = aiData?.sections || [];
      const benchmarkSection =
        sections.find((s) => s?.['fund-benchmarks']) || {};
      benchmarks.push(...(benchmarkSection?.['fund-benchmarks']?.values || []));
    } catch (e) {
      reportException(
        e,
        'find benchmarks exception for generate historical track record'
      );
    }

    // 3. Prepare chart data
    const chartData = {
      labels: [],
      values: [],
      dates: new Set(),
    };

    benchmarks.forEach((benchmark) => {
      try {
        // Clean and validate name
        const name = String(benchmark?.benchmark_fund_name || '')
          .replace(/[^\w\s-]/g, '') // Remove special chars
          .trim();

        // Clean and validate value
        const valueStr = String(benchmark?.benchmark_net_irr_in_percent || '0')
          .replace('%', '')
          .replace(',', '')
          .split(' ')[0]; // Take only the first part if there are notes

        let value = parseFloat(valueStr) || 0;

        // Handle cases where percentage might be incorrectly scaled (like 4750% meaning 47.5%)
        if (value > 100) {
          value = value / 100;
        }

        if (name && value !== undefined) {
          chartData.labels.push(name);
          chartData.values.push(value);
          if (benchmark.as_of_date) {
            chartData.dates.add(`As of: ${benchmark.as_of_date}`);
          }
        }
      } catch (e) {
        reportException(
          e,
          'parse benchmarks for generate historical track record'
        );
      }
    });

    // 4. Handle empty data case
    if (chartData.labels.length === 0) {
      slide.addText('No valid benchmark data available', {
        x: 0.5,
        y: 1,
        fontSize: 14,
        color: '666666',
      });
      return;
    }

    // 5. Create the chart with safe defaults
    try {
      slide.addChart(
        pptx.ChartType.bar,
        [
          {
            name: 'Net IRR (%)',
            labels: chartData.labels,
            values: chartData.values,
          },
        ],
        {
          x: 0.5,
          y: 1,
          w: '90%',
          h: 4,
          barDir: 'col',
          barGrouping: 'clustered',
          valAxisTitle: 'Net IRR (%)',
          valAxisMinVal: Math.min(0, ...chartData.values),
          valAxisMaxVal: Math.max(10, ...chartData.values) * 1.2, // Minimum 10% scale
          showValue: true,
          dataLabelFormatCode: '0.0', // Shows as percentage (e.g., 15.5%)
          dataLabelPosition: 'outEnd',
          chartColors: ['#418ab3'], // Single color for all bars
          barSize: 40,
        }
      );

      // 6. Add date footnote if available
      if (chartData.dates.size > 0) {
        slide.addText([...chartData.dates].join(' | '), {
          x: 0.5,
          y: 5.2,
          fontSize: 9,
          color: '777777',
          w: '90%',
        });
      }
    } catch (e) {
      reportException(e, 'add chart to generate historical track record');
      slide.addText('Chart could not be displayed', {
        x: 0.5,
        y: 1,
        fontSize: 14,
        color: 'FF0000',
      });
    }
  };

  const generateFundCompare = (
    pptx,
    fundCompareData,
    SelectedOptions,
    fundKey
  ) => {
    const slide = pptx.addSlide();
    slide.addText('Fund Comparison', {
      x: 0.5,
      y: 0.3,
      fontSize: 20,
      bold: true,
    });

    if (!fundCompareData || fundCompareData.length === 0) {
      reportError('No fund comparison data available', 'generate fund compare');
      slide.addText('No fund comparison data available', {
        x: 0.5,
        y: 1,
        fontSize: 14,
        color: '363636',
      });
      return;
    }

    // Prepare the data - more robust extraction
    const rawData = fundCompareData
      .map((fund) => {
        try {
          // Extract values from sections with better error handling
          const getValue = (sectionKey, fieldKey) => {
            try {
              const section = fund.sections?.find((s) => s[sectionKey]);
              if (!section) return '';

              const items = Array.isArray(section[sectionKey])
                ? section[sectionKey]
                : section[sectionKey]?.values || [];

              const item = items.find((i) => i[fieldKey] !== undefined);
              const value = item ? item[fieldKey] : '';

              return value === null || value === 'null' ? '' : value;
            } catch (e) {
              reportException(
                e,
                `get value for fund compare for ${sectionKey}.${fieldKey}`
              );
              return '';
            }
          };

          // Extract fee values specifically
          const getFeeValue = (feeType) => {
            try {
              const feeSection = fund.sections?.find((s) => s['fund-fees']);
              if (!feeSection) return '';

              const fees = Array.isArray(feeSection['fund-fees'])
                ? feeSection['fund-fees']
                : feeSection['fund-fees']?.values || [];

              return fees
                .filter((f) => f.fee_type === feeType)
                .map((f) => f.fee_value || '')
                .join('\n');
            } catch (e) {
              reportException(
                e,
                `get fee value for fund compare for ${feeType}`
              );
              return '';
            }
          };

          return {
            'Fund Name': fund.fund_name || '',
            'Inception Year': getValue('fund-details', 'inception_year'),
            Size: getValue('fund-details', 'size'),
            'Max Size': getValue('fund-details', 'max_fund_size'),
            'Investment Period': getValue('fund-terms', 'investment_period'),
            Duration: getValue('fund-terms', 'fund_duration'),
            Extension: getValue('fund-terms', 'extension'),
            'Management Fee': getFeeValue('Management Fee'),
            'Carried Interest': getValue('fund-terms', 'carried_interest'),
            'Preferred Return': getValue('fund-terms', 'preferred_return'),
            'GP Commitment': getValue('fund-terms', 'gp_commitment'),
          };
        } catch (e) {
          reportException(e, 'process fund for generate fund compare');
          return null;
        }
      })
      .filter((fund) => fund !== null); // Remove any null entries from errors

    if (rawData.length === 0) {
      reportError(
        'No valid comparison data after processing',
        'generate fund compare'
      );
      slide.addText('No valid comparison data found', {
        x: 0.5,
        y: 1,
        fontSize: 14,
        color: '363636',
      });
      return;
    }

    // Get all property keys (excluding Fund Name which will be column headers)
    const propertyKeys = Object.keys(rawData[0]).filter(
      (key) => key !== 'Fund Name'
    );

    // Prepare table rows - first row is headers
    const tableRows = [
      ['Property', ...rawData.map((fund) => fund['Fund Name'])],
    ];

    // Add each property as a row
    propertyKeys.forEach((property) => {
      const row = [property];
      rawData.forEach((fund) => {
        row.push(fund[property] || 'N/A');
      });
      tableRows.push(row);
    });

    // Add the table to the slide with better formatting
    try {
      slide.addTable(tableRows, {
        x: 0.5,
        y: 1,
        w: '90%',
        colW: [2.0, ...Array(rawData.length).fill(2.5)], // Wider columns for fund data
        fontSize: 10,
        border: { type: 'solid', color: '888888', pt: 1 },
        align: 'left',
        valign: 'middle',
        autoPageHeaderRows: 1,
        headerRows: 1,
        headerRowColor: 'F2F2F2',
        headerRowFontFace: 'Arial',
        headerRowFontSize: 12,
        headerRowBold: true,
        margin: 4,
        // Alternate row coloring for better readability
        rowH: 0.3,
        fill: { color: 'F8F8F8' },
        color: '404040',
        // Highlight header
        headerFontColor: '000000',
        headerFill: { color: 'E0E0E0' },
        // Ensure text wrapping
        wrapText: true,
        lineSpacing: 12,
      });
    } catch (e) {
      reportException(e, 'add table to slide for generate fund compare');
      slide.addText('Error displaying comparison table', {
        x: 0.5,
        y: 1,
        fontSize: 14,
        color: 'FF0000',
      });
    }
  };

  const generatePPT = async (fundBenchmark, matrixData, fundDetailJson) => {
    const pptx = new PptxGenJS();

    sectionsConfig.forEach((section) => {
      if (section.key === 'recommendation') {
        generateRecommendationSlide(
          pptx,
          fundData,
          displayUserConfig,
          getValueByKey,
          formatAmountMagnitude
        );
        return;
      }

      if (section.key === 'executive_summary') {
        generateExecutiveSummarySlide(pptx, fundData, divisionData);
        return;
      }

      if (section.key === 'benchmark_quartiles') {
        generateBenchmarkSlide(pptx, fundBenchmark, sectionsConfig);
        return;
      }

      if (section.key === 'scoring_matrix') {
        generateScoringMatrixSlide(pptx, matrixData);
        return;
      }

      if (section.key === 'value_creation') {
        generateValueCreationSlide(pptx, fundDetailJson, sectionsConfig);
        return;
      }

      if (section.key === 'performance_expectations') {
        generatePerformanceExpectationsSlide(
          pptx,
          fundDetailJson,
          sectionsConfig,
          divisionData
        );
        return;
      }

      if (section.key === 'historical_fund_track_record') {
        generateHistoricalTrackRecord(pptx, fundData);
        return;
      }

      if (section.key === 'fund_comparison') {
        generateFundCompare(pptx, fundCompareData, SelectedOptions, fundKey);
        return;
      }

      const slide = pptx.addSlide();
      const title = section.title || getTitleByKey(section.key) || section.key;
      const content = getSectionTextContent(section.key); // fallback for other sections

      slide.addText(title, { x: 0.5, y: 0.3, fontSize: 20, bold: true });

      slide.addText(content.substring(0, 3000), {
        x: 0.5,
        y: 1,
        fontSize: 14,
        color: '363636',
        lineSpacing: 20,
        w: '90%',
        h: '80%',
        wrap: true,
      });
    });

    await pptx.writeFile(`IC-Report${fundData?.fund_name || 'report'}.pptx`);
    window.close();
    return true;
  };

  const getSectionTextContent = (sectionKey) => {
    if (!Array.isArray(fundDetailJson)) return '[No data]';

    let text = '';

    for (const section of fundDetailJson) {
      if (section.hasOwnProperty(sectionKey)) {
        const sectionData = section[sectionKey];

        if (Array.isArray(sectionData)) {
          sectionData.forEach((item) => {
            Object.entries(item).forEach(([key, val]) => {
              if (val && val !== 'null') {
                text += `${getTitleByKey(key) || key}: ${val}\n`;
              }
            });
          });
        } else if (sectionData?.values) {
          sectionData.values.forEach((item) => {
            Object.entries(item).forEach(([key, val]) => {
              if (val && val !== 'null') {
                text += `${getTitleByKey(key) || key}: ${val}\n`;
              }
            });
          });
        }
      }
    }

    return text || '[No data]';
  };

  return (
    <div
      className="content-section"
      style={{
        background: 'var(--bgAccent)',
        overflowY: isBlackBookUrl ? 'auto' : 'visible',
        maxHeight: isBlackBookUrl ? '100vh' : 'none',
      }}
    >
      <div className="header-web-wrap">
        <div className="flex-web">
          <div className="logo" onClick={() => onMain()}>
            <img src={Logo} className="mainLogo" alt="main-logo" />
          </div>
          <div className="header-title">
            <p className="web-title fund-name">{fundData?.fund_name}</p>
          </div>
          <div className="header-action">
            {directCall && fundData?.fund_status === 'CIO Approval' && (
              <>
                <button onClick={() => sendUpdateRequest('Committed')}>
                  Approve
                </button>
                <button
                  className="reject-btn"
                  onClick={() => sendUpdateRequest('Rejected')}
                >
                  Reject
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      {Array.isArray(sectionsConfig) &&
        sectionsConfig.map((section) =>
          section ? (
            <React.Fragment key={section.key}>
              {renderSectionComponent(section.key)}
            </React.Fragment>
          ) : null
        )}
    </div>
  );
};

export default PdfViewer;
